import { useEffect, useMemo, useState } from 'react';
import { autoplannerAPI, planningAPI } from '../../../services/api';
import CrewLineupEditor from '../../planner/ScheduleModal/CrewLineupEditor';
import { getBoatTypeLayout, loadBoatTypes } from '../../../utils/boatTypes';

const AutoplannerResults = ({ taskId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [sessionsMeta, setSessionsMeta] = useState({});

  // Local working state: selection per session and editable lineups per (session, crew)
  const [selectedOptions, setSelectedOptions] = useState({}); // session_id -> option index
  const [lineupsBySession, setLineupsBySession] = useState({}); // session_id -> { crew_id -> lineupArray }

  useEffect(() => {
    loadBoatTypes().catch(() => {});
  }, []);

  useEffect(() => {
    const fetchResult = async () => {
      if (!taskId) return;
      try {
        setLoading(true);
        const data = await autoplannerAPI.getAutoplannerResult(taskId);
        setResult(data || null);

        // Load session details for titles/durations in summary cards
        const sessionIds = Array.from(
          new Set(
            [
              ...(data?.proposals || []).map((p) => p.session_id),
              ...(data?.non_schedulable || []).map((item) => item.session_id),
            ].filter((id) => id != null)
          )
        );
        if (sessionIds.length > 0) {
          try {
            const meta = await autoplannerAPI.getSessionsByIds(sessionIds);
            const sessionsArray = Array.isArray(meta) ? meta : meta?.sessions || [];
            const map = {};
            sessionsArray.forEach((s) => {
              // Normalize assigned crews for easier lookup
              const assigned = (s.crew_assignments || []).map((ca) => ({
                id: ca.crew_id,
                name: ca.crew?.name || `Crew ${ca.crew_id}`,
                boat_type: ca.crew?.boat_type || '8+',
              }));
              map[s.id] = { ...s, assigned_crews: assigned };
            });
            setSessionsMeta(map);
          } catch (e) {
            // Non-fatal
          }
        }

        // Initialize lineups and option selections from proposals (use boat_type for sizing)
        const initialSelected = {};
        const initialLineups = {};
        (data?.proposals || []).forEach((proposal) => {
          const sId = proposal.session_id;
          initialSelected[sId] = 0; // pick first option by default
          const option = (proposal.options || [])[0];
          const crews = option?.crews || [];
          if (!initialLineups[sId]) initialLineups[sId] = {};
          crews.forEach((c) => {
            const crewId = c.crew_id;
            const boatType =
              c.boat_type ||
              (sessionsMeta[sId]?.assigned_crews || []).find((ac) => ac.id === crewId)?.boat_type ||
              '8+';
            const meta = getBoatTypeLayout(boatType);
            const totalPositions = (meta.seats || 0) + (meta.hasCox ? 1 : 0);
            const arr = new Array(totalPositions > 0 ? totalPositions : 1).fill(null);
            let nextSeat = 1;
            (c.lineup || []).forEach((entry) => {
              const seatNum =
                typeof entry.seat_number === 'number' ? entry.seat_number : nextSeat++;
              const idx = meta.hasCox ? seatNum : Math.max(0, seatNum - 1);
              if (idx >= 0 && idx < arr.length) {
                arr[idx] = {
                  id: entry.rower_id,
                  first_name: `R${entry.rower_id}`,
                  last_name: '',
                  preferred_side: entry.side || 'none',
                };
              }
            });
            initialLineups[sId][crewId] = arr;
          });
        });
        setSelectedOptions(initialSelected);
        setLineupsBySession(initialLineups);
      } catch (e) {
        console.error('Error loading autoplanner result:', e);
        setError('Failed to load results.');
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [taskId]);

  const proposals = useMemo(() => {
    const proposalsArray = result?.proposals || [];
    // Sort proposals by date to ensure consistent ordering
    return proposalsArray.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  }, [result]);

  const handleOptionChange = (sessionId, optIdx) => {
    setSelectedOptions((prev) => ({ ...prev, [sessionId]: optIdx }));

    // Reset lineups to chosen option's default (use boat_type)
    const proposal = proposals.find((p) => p.session_id === sessionId);
    const option = proposal?.options?.[optIdx];
    if (!option) return;

    setLineupsBySession((prev) => {
      const clone = { ...prev };
      if (!clone[sessionId]) clone[sessionId] = {};
      option.crews?.forEach((c) => {
        const crewId = c.crew_id;
        const boatType =
          c.boat_type ||
          (sessionsMeta[sessionId]?.assigned_crews || []).find((ac) => ac.id === crewId)
            ?.boat_type ||
          '8+';
        const meta = getBoatTypeLayout(boatType);
        const totalPositions = (meta.seats || 0) + (meta.hasCox ? 1 : 0);
        const arr = new Array(totalPositions > 0 ? totalPositions : 1).fill(null);
        let nextSeat = 1;
        (c.lineup || []).forEach((entry) => {
          const seatNum = typeof entry.seat_number === 'number' ? entry.seat_number : nextSeat++;
          const idx = meta.hasCox ? seatNum : Math.max(0, seatNum - 1);
          if (idx >= 0 && idx < arr.length) {
            arr[idx] = {
              id: entry.rower_id,
              first_name: `R${entry.rower_id}`,
              last_name: '',
              preferred_side: entry.side || 'none',
            };
          }
        });
        clone[sessionId][crewId] = arr;
      });
      return clone;
    });
  };

  const handleLineupChange = (sessionId, crewId, newLineupArray) => {
    setLineupsBySession((prev) => ({
      ...prev,
      [sessionId]: { ...(prev[sessionId] || {}), [crewId]: newLineupArray },
    }));
  };

  const handleSaveAll = async () => {
    try {
      setLoading(true);

      // For every session proposal, schedule the chosen option via planner endpoint
      for (const p of proposals) {
        const sessionId = p.session_id;
        const chosenIdx = selectedOptions[sessionId] ?? 0;
        const option = p.options?.[chosenIdx];
        if (!option) continue;

        // Build payload for /planning/sessions/{session_id}/schedule (session splitting)
        const scheduleData = {
          crew_ids: (option.crews || []).map((c) => c.crew_id),
          coaches: (option.coaches || []).map((uid) => ({ user_id: uid, role: 'coach' })),
          start_time: option.start_time,
          date: p.date,
          lineups: {},
          autoplanner_task_id: taskId,
        };

        // Map crew -> boat_type for seat numbering
        const crewBoatType = new Map(
          (option.crews || []).map((c) => [c.crew_id, c.boat_type || '8+'])
        );

        // Translate lineup arrays back into required objects
        const perCrew = lineupsBySession[sessionId] || {};
        Object.entries(perCrew).forEach(([crewIdStr, arr]) => {
          const crewId = parseInt(crewIdStr, 10);
          const boatType = crewBoatType.get(crewId) || '8+';
          const meta = getBoatTypeLayout(boatType);
          const lineup = [];
          (arr || []).forEach((rower, idx) => {
            if (!rower) return;
            const seat_number = meta.hasCox ? idx : idx + 1;
            lineup.push({ rower_id: rower.id, seat_number, side: rower.preferred_side || 'none' });
          });
          scheduleData.lineups[crewId] = lineup;
        });

        await planningAPI.scheduleSessionCrews(sessionId, scheduleData);
      }

      // Mark the task as scheduled (final state) using existing endpoint
      try {
        await planningAPI.markTaskSessionsScheduled?.(taskId);
      } catch (e) {
        // Non-fatal; individual schedule calls already try to set the status
      }

      // Refresh task after save
      try {
        await autoplannerAPI.getAutoplannerTask(taskId);
      } catch (_) {
        void _;
      }

      setError(null);
    } catch (e) {
      console.error('Error saving sessions:', e);
      setError(e?.response?.data?.detail || 'Failed to save sessions.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="flex items-center space-x-3 text-sm text-gray-700">
          <svg className="h-5 w-5 animate-spin text-blue-600" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading results…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!result) {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-6 text-sm text-yellow-800">
        No results available for this task.
      </div>
    );
  }

  const scheduled = result.scheduled_sessions || [];
  const hasProposals = (result.proposals || []).length > 0;

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-lg font-semibold text-gray-900">Autoplanner Results</h3>
        {result.summary && <p className="mt-2 text-sm text-gray-700">{result.summary}</p>}
      </div>

      {(result.non_schedulable || []).length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 shadow-sm">
          <h4 className="text-base font-semibold text-amber-900">Cannot schedule</h4>
          <p className="mt-1 text-sm text-amber-800">
            These crews are excluded from Save All. Review the blocking reasons and adjust
            lineups, availability, or session choices before running the planner again.
          </p>
          <div className="mt-4 space-y-3">
            {(result.non_schedulable || []).map((item) => {
              const session = sessionsMeta[item.session_id];
              const sessionTitle = session?.title || item.session_title || `Session ${item.session_id}`;

              return (
                <div
                  key={`${item.session_id}-${item.crew_id}`}
                  className="rounded-md border border-amber-200 bg-white p-4"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.crew_name}</div>
                      <div className="text-xs text-gray-600">{sessionTitle}</div>
                    </div>
                    <div className="text-right text-xs font-medium text-amber-800">
                      {item.best_overlap}
                    </div>
                  </div>
                  <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700">
                    {(item.blocking_reasons || []).map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                  {(item.missing_seats || []).length > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      Missing seats: {(item.missing_seats || []).join(', ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {hasProposals ? (
        <div className="space-y-6">
          {proposals.map((p) => {
            const session = sessionsMeta[p.session_id];
            const chosenIdx = selectedOptions[p.session_id] ?? 0;
            const option = p.options?.[chosenIdx];
            return (
              <div key={p.session_id} className="rounded-lg bg-white p-6 shadow">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-600">{p.date}</div>
                    <div className="text-base font-medium text-gray-900">
                      {session?.title || `Session ${p.session_id}`}
                    </div>
                  </div>
                  <div>
                    <label className="mr-2 text-sm text-gray-700">Time option</label>
                    <select
                      className="rounded border border-gray-300 p-2 text-sm"
                      value={chosenIdx}
                      onChange={(e) =>
                        handleOptionChange(p.session_id, parseInt(e.target.value, 10))
                      }
                    >
                      {(p.options || []).map((opt, idx) => (
                        <option key={idx} value={idx}>
                          {opt.start_time} • score {opt.score}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Training group coaches (IDs come from backend; coach_display has names via API enrichment) */}
                {(() => {
                  const coachesUi = option?.coach_display;
                  const rawIds = option?.coaches || [];
                  const chips = Array.isArray(coachesUi)
                    ? coachesUi
                    : rawIds.map((uid) => ({
                        user_id: uid,
                        name: `Coach #${uid}`,
                      }));
                  if (!chips.length) return null;
                  return (
                    <div className="mb-4 rounded-lg border border-emerald-100 bg-emerald-50/80 p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-800">
                        Coaches
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {chips.map((c) => (
                          <span
                            key={c.user_id}
                            className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-3 py-1 text-sm text-emerald-900 shadow-sm"
                            title={`User ID ${c.user_id}`}
                          >
                            {c.name}
                          </span>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-emerald-800/90">
                        These coaches are assigned for this proposal when you save (training group coaches).
                      </p>
                    </div>
                  );
                })()}

                {/* Crew lineup editors */}
                <div className="space-y-6">
                  {(option?.crews || []).map((c) => {
                    const lineupArray = lineupsBySession[p.session_id]?.[c.crew_id] || [];
                    const fakeCrew = {
                      id: c.crew_id,
                      name:
                        (session?.assigned_crews || []).find((ac) => ac.id === c.crew_id)?.name ||
                        `Crew ${c.crew_id}`,
                      boat_type:
                        c.boat_type ||
                        (session?.assigned_crews || []).find((ac) => ac.id === c.crew_id)
                          ?.boat_type ||
                        '8+',
                      side_preference_strict: false,
                      rigging_config: null,
                    };
                    // We do not have availability lists at this step; editor still supports rearranging seats
                    const availableRowers = lineupArray.filter(Boolean).map((r) => ({
                      id: r.id,
                      first_name: r.first_name,
                      last_name: r.last_name,
                      preferred_side: r.preferred_side,
                      crew_memberships: [c.crew_id],
                    }));

                    // Build assignedRowers set for the editor
                    const assignedSet = new Set(availableRowers.map((r) => r.id));

                    return (
                      <div key={c.crew_id} className="rounded border border-gray-200 p-4">
                        <div className="mb-3 text-sm font-medium text-gray-900">
                          {fakeCrew.name}
                        </div>
                        <CrewLineupEditor
                          crew={fakeCrew}
                          availableRowers={availableRowers}
                          crewLineup={lineupArray}
                          onLineupChange={(crewId, arr) =>
                            handleLineupChange(p.session_id, crewId, arr)
                          }
                          assignedRowers={assignedSet}
                          onShowAllChange={() => {}}
                          showAll={false}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex justify-end">
            <button
              onClick={handleSaveAll}
              className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
            >
              Save All Sessions
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-white p-6 shadow">
          <h4 className="mb-3 text-sm font-medium text-gray-900">
            Scheduled Sessions Preview ({scheduled.length})
          </h4>
          <ul className="space-y-2 text-sm text-gray-700">
            {scheduled.map((s, idx) => (
              <li key={idx} className="rounded border border-gray-200 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Session {s.session_id}</div>
                    <div className="text-xs text-gray-600">
                      {s.scheduled_date} at {s.scheduled_time}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">Crew {s.crew_id}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AutoplannerResults;
